import { useCallback } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { DatabaseEngineId } from '@/core/engines';
import type { DatabaseObject } from '../models/databaseObjectTypes';
import { DatabaseObjectType } from '../models/databaseObjectTypes';
import databaseObjectsService from '../services/databaseObjectsService';
import type { GetDatabasesResult } from '../services/databasesService';
import { fetchObjectsForFolder } from '../services/objectFetchService';
import type { ExplorerFetchOrigin } from '../services/explorerQueryUtils';
// Shared key fns — `pinResolution.ts` reads from the same cache and must
// agree on the exact key shape. Keep this import to make the dependency
// explicit; renaming the keys here surfaces as a build break at every
// consumer.
import { databaseObjectsQueryKey } from '../services/databaseObjectsQueryKeys';
import { getSharedQueryClient } from '../../../core/query/sharedQueryClient';
// The "may this connection use the shared slot?" rule lives with the key, so the
// non-React reader below cannot drift from what `useDatabaseList` does. Its
// liveness half needs the connection store — read lazily, exactly as
// `databasesService` already reads it, so this stays an import-time leaf.
import {
  canShareDatabaseListing,
  databaseListQueryKey,
  isConnectionLive,
} from '../services/databaseListSharing';
import { useConnectionStore } from '../../connections/store/connectionStore';

const databaseKey = (connectionId: string) =>
  databaseObjectsQueryKey.databases(connectionId);

const objectKey = (
  objectType: DatabaseObjectType,
  connectionId: string,
  database: string
) => databaseObjectsQueryKey.objects(objectType, connectionId, database);

function assertDatabaseName(database: string) {
  if (!database) {
    throw new Error('Database name is required');
  }
}

/**
 * The origin of the fetch currently in flight for a query key.
 *
 * 1.4.21 second review R1. `objectPreloadService` fires every
 * `(objectType, database)` key of a connection at once, so for the whole
 * preload every key is `fetching` — and react-query dedupes by key: a user's
 * folder expand that lands on one of those keys JOINED the preload's promise,
 * whose query sits in the SQL Tools adapter's preload gate behind up to
 * D × 5 queued logins. The gate only ever saw the preload's own execution
 * id, so it could not help: the click never issued a query of its own.
 *
 * Recording which origin is in flight lets a user fetch bypass the dedupe
 * for exactly that case — its own (ungated) query, its own result, written
 * straight into the cache. Keyed by the stringified query key because the
 * map has to agree with react-query's own identity for the key, and set /
 * cleared inside the queryFn so it is exactly "a fetch is running".
 */
const inFlightFetchOrigins = new Map<string, { origin: ExplorerFetchOrigin; token: symbol }>();

/**
 * Thrown instead of returning rows from a fetch whose connect ended. Callers
 * that draw into the tree fence on their own generation anyway; this makes the
 * obsolete case impossible to consume by accident.
 */
export const OBSOLETE_EXPLORER_FETCH = 'OBSOLETE_EXPLORER_FETCH';

export function createObsoleteExplorerFetchError(): Error {
  const error = new Error('Explorer fetch discarded: its connection was replaced');
  error.name = OBSOLETE_EXPLORER_FETCH;
  return error;
}

export function isObsoleteExplorerFetchError(error: unknown): boolean {
  return error instanceof Error && error.name === OBSOLETE_EXPLORER_FETCH;
}
const inFlightKeyOf = (queryKey: readonly unknown[]): string => JSON.stringify(queryKey);

async function fetchObjectsOrThrow(
  objectType: DatabaseObjectType,
  connectionId: string,
  database: string,
  engine: DatabaseEngineId | string | undefined,
  origin: ExplorerFetchOrigin,
): Promise<DatabaseObject[]> {
  const { objects, error } = await fetchObjectsForFolder(objectType, connectionId, database, engine, origin);
  if (error) throw new Error(error);
  return objects;
}

/**
 * Fetch database objects through the react-query cache — the function behind
 * `useDatabaseQueries().fetchObjects`, exported so the forced-STS burst spec
 * can drive the real cache path from the renderer.
 *
 * @param origin `'preload'` when the background preload asks (F3); a user
 *   expand (`'user'`, the default) that lands on a key the PRELOAD is
 *   fetching does NOT wait for it (R1, above) — any other in-flight fetch is
 *   shared as before.
 */
export async function fetchObjectsThroughCache(
  queryClient: QueryClient,
  objectType: DatabaseObjectType,
  connectionId: string,
  database: string,
  engine?: DatabaseEngineId | string,
  origin?: ExplorerFetchOrigin,
  /**
   * "The connect this fetch was started for is still live." Only the
   * preload-bypass branch needs it — everything else goes through
   * `fetchQuery`, which `cancelQueries` can reach.
   */
  isCurrent?: () => boolean,
): Promise<DatabaseObject[]> {
  assertDatabaseName(database);
  const queryKey = objectKey(objectType, connectionId, database);
  const keyString = inFlightKeyOf(queryKey);
  const fetchOrigin: ExplorerFetchOrigin = origin ?? 'user';

  if (fetchOrigin !== 'preload' && inFlightFetchOrigins.get(keyString)?.origin === 'preload') {
    // This branch runs OUTSIDE `fetchQuery`, so `cancelQueries` cannot reach
    // it. Two ownership checks bound it.
    //
    // The cache entry: ending a connect removes the connection's queries, and
    // the successor's `fetchQuery` creates a NEW `Query` for the same key. A
    // swap means these rows belong to a connection that is gone. `undefined`
    // is NOT an identity — a lingering preload-origin marker can select this
    // branch with no `Query` at all, and `undefined === undefined` would then
    // admit a write across a disconnect the successor reads back as fresh.
    const cache = queryClient.getQueryCache();
    const queryAtStart = cache.find({ queryKey });
    // The caller's own fence, so an obsolete result is never even RETURNED —
    // the folder loader would write it straight into the successor's tree.
    const wasCurrent = isCurrent ?? (() => true);
    const objects = await fetchObjectsOrThrow(objectType, connectionId, database, engine, fetchOrigin);
    if (!wasCurrent()) throw createObsoleteExplorerFetchError();
    if (queryAtStart !== undefined && cache.find({ queryKey }) === queryAtStart) {
      // The preload's own result lands later and overwrites this with the same
      // list; until then every reader (the tree, pin resolution, search) sees
      // the user's answer.
      queryClient.setQueryData(queryKey, objects);
    }
    return objects;
  }

  return queryClient.fetchQuery({
    queryKey,
    queryFn: async () => {
      // Identity-tagged: a cancelled predecessor's `finally` runs AFTER the
      // successor has claimed the same key (a reconnect cancels the dead
      // connect's queries), and an untagged delete would clear the successor's
      // 'preload' marker — silently re-enabling the dedupe a user's expand is
      // meant to bypass.
      const token = Symbol('explorer-fetch');
      inFlightFetchOrigins.set(keyString, { origin: fetchOrigin, token });
      try {
        return await fetchObjectsOrThrow(objectType, connectionId, database, engine, fetchOrigin);
      } finally {
        if (inFlightFetchOrigins.get(keyString)?.token === token) {
          inFlightFetchOrigins.delete(keyString);
        }
      }
    },
    staleTime: 30_000,
  });
}

/**
 * Mark a connection's shared database listing stale from a NON-React context.
 *
 * The listing lives under one key per connection (`databaseKey`) and is read by
 * every database picker through `useDatabaseList`, so code that changes the set
 * of databases needs a way to say so without being a hook. Invalidate (not
 * remove): the rows on screen stay renderable while the next read re-queries.
 *
 * No-ops before `setSharedQueryClient` has run (unit tests, the web build's
 * pre-mount window) — a missing client can only mean nothing has read the cache
 * yet either.
 */
export function invalidateDatabaseListing(connectionId: string | null | undefined): void {
  if (!connectionId) return;
  void getSharedQueryClient()?.invalidateQueries({ queryKey: databaseKey(connectionId) });
}

/**
 * Read a connection's database listing from a NON-React context, THROUGH the
 * shared cache slot — the read-side sibling of {@link invalidateDatabaseListing}.
 *
 * For the handful of callers that need the list as *data* rather than as a menu
 * (`contextSelectionStore`, `useContextResolver`, `resolveDatabase`,
 * `addTileDatabaseDefault`, `useLoginUserMappings`). They used to call
 * `getDatabases` straight, which meant their round-trip was invisible to the
 * cache: it neither joined an in-flight listing nor left anything behind for the
 * picker the user opens next.
 *
 * `staleTime: 0` keeps the revalidation semantics identical to `useDatabaseList`
 * — a resolver still gets a REAL listing every time, never a cached answer of
 * unknown age, which matters because these paths decide things like "does this
 * database still exist on the new connection?". What routing through the cache
 * buys is therefore precisely two things, and it is worth being exact about it:
 *
 *  1. **Dedupe** — react-query joins a listing already in flight for the same
 *     key instead of issuing a second identical server query.
 *  2. **Slot warming** — the answer lands in the one entry every picker reads,
 *     so the next dropdown opens onto rows instead of "Loading databases…".
 *
 * Falls back to a direct call when there is no shared client (unit tests, the
 * pre-mount window) or when the connection may not share the slot
 * (`canShareDatabaseListing`: probe ids, `ignoreConnectionLimit` listings, and a
 * connection with no live connect — the resolver still gets its real listing, it
 * just does not write a session-retained entry for a connect that is not running).
 */
export async function readDatabaseListingThroughCache(
  connectionId: string,
  engine?: DatabaseEngineId | string,
): Promise<GetDatabasesResult> {
  const client = getSharedQueryClient();
  const connectionIsLive = isConnectionLive(
    useConnectionStore.getState().activeConnections,
    connectionId,
  );
  if (!client || !canShareDatabaseListing(connectionId, { connectionIsLive })) {
    return databaseObjectsService.getDatabases(connectionId, engine);
  }
  return client.fetchQuery({
    queryKey: databaseKey(connectionId),
    queryFn: () => databaseObjectsService.getDatabases(connectionId, engine),
    staleTime: 0,
    // Retained for the session — see `fetchDatabases` below.
    gcTime: Infinity,
  });
}

export function useDatabaseQueries() {
  const queryClient = useQueryClient();

  /**
   * Fetch databases for a connection.
   *
   * Slice 5b: returns the full {@link GetDatabasesResult} shape (including
   * `state`, `error`, `durationMs`) instead of throwing on `error` and
   * discarding the array. Consumers handle the `state` themselves so the
   * Object Explorer tree, Table Explorer dropdown, and other surfaces can
   * render *the same affordance* on the same underlying state. Throwing
   * silently discarded the structured error info the diagnostics dialog
   * needs and was the source of the dropdown / tree disagreement.
   *
   * SHARING. Like every other producer of the listing this goes through
   * `databaseListSharing`, so "the shared slot only ever holds a listing taken
   * during a live connect" is true BY CONSTRUCTION rather than by argument. That
   * matters here because this is the Object Explorer's own producer and its
   * callers are not all on-connect paths: `useNodeRefresh.refreshDatabasesFolder`
   * (right-click → Refresh), the databases-folder expand
   * (`useNodeExpansion` → `listDatabasesFolder`) and the failure tile's Retry
   * (`DatabaseExplorer`'s `'retry-list-databases'` node action) all reach it for
   * whatever connection the tree happens to show — and the tree keeps a collapsed
   * Databases folder for a DISCONNECTED connection, so each of those can ask for a
   * listing outside a live connect, which usually answers `NO_POOL`.
   *
   * It is belt AND braces, deliberately — not redundancy to delete later. The
   * review that added the gate also established that the ungated write was
   * survivable: nothing READS the shared slot for a not-live id (`useDatabaseList`
   * is on the unshared key there, `readDatabaseListingThroughCache` goes direct),
   * every path from not-live to live crosses an eviction boundary
   * (`connectionListingLifecycle`, boundary 4), and a write in flight AT the
   * boundary is cancelled with its late result dropped. The gate removes the need
   * to re-derive that five-link argument every time someone reads this file, and
   * closes it against a future reader of the slot who does not know the rule.
   *
   * A not-live listing still HAPPENS and still returns its real error — it is
   * routed to the unshared key with `gcTime: 0`, exactly as `useDatabaseList` does,
   * so the Explorer's failure tile and diagnostics are unchanged.
   *
   * @param connectionId Connection ID
   * @param engine Database engine (defaults to 'mssql')
   */
  const fetchDatabases = useCallback(
    async (
      connectionId: string,
      engine?: DatabaseEngineId | string
    ): Promise<GetDatabasesResult> => {
      // Read at CALL time, not through a hook selector: liveness as of this fetch
      // is what decides the key, and subscribing here would rebuild the callback —
      // and re-run the Explorer effects that depend on it — on every store tick.
      const connectionIsLive = isConnectionLive(
        useConnectionStore.getState().activeConnections,
        connectionId,
      );
      const shared = canShareDatabaseListing(connectionId, { connectionIsLive });
      return queryClient.fetchQuery({
        queryKey: databaseListQueryKey(connectionId, { connectionIsLive }),
        queryFn: () => databaseObjectsService.getDatabases(connectionId, engine),
        // 30s for a listing that SUCCEEDED, 0 for one that did not. The slot is
        // shared now, so a picker's transient `'error'` sits in the same entry the
        // Explorer reads — and under a flat 30s `staleTime` a tree rebuild inside
        // that window was served the failure and painted the error tile until the
        // user pressed Retry. A failed listing is never worth reusing; only a real
        // one is, which is what the 30s window is for (the connect's listing being
        // reused by the preload wave that follows it).
        staleTime: (query) => (query.state.data?.state === 'ok' ? 30_000 : 0),
        // SHARED: this write has NO observer — `fetchQuery` schedules GC the moment
        // it settles — and closed pickers are not observers either
        // (`useDatabaseList` passes `subscribed: false`). Under the default
        // 5-minute `gcTime` the listing the Explorer tree is still displaying
        // would be collected out from under it, and the next picker opened on an
        // idle connection would paint "Loading databases…" again. The entry's end
        // of life is an event, not a timer: `connectionListingLifecycle` removes
        // it when the connect ends, `updateConnection` when an edit changes what
        // `getDatabases` returns. See `useDatabaseList` → RETENTION.
        // UNSHARED: per-call data nothing reads back, so it must not outlive the
        // call.
        gcTime: shared ? Infinity : 0,
      });
    },
    [queryClient]
  );

  /**
   * Fetch database objects (tables, views, etc.) — see `fetchObjectsThroughCache`.
   * @param objectType Type of objects to fetch
   * @param connectionId Connection ID
   * @param database Database name
   * @param engine Database engine (defaults to 'mssql')
   * @param origin `'preload'` when the background preload asks (F3)
   */
  const fetchObjects = useCallback(
    (
      objectType: DatabaseObjectType,
      connectionId: string,
      database: string,
      engine?: DatabaseEngineId | string,
      origin?: ExplorerFetchOrigin,
      isCurrent?: () => boolean,
    ) => fetchObjectsThroughCache(queryClient, objectType, connectionId, database, engine, origin, isCurrent),
    [queryClient]
  );

  const invalidateDatabases = useCallback(
    (connectionId: string) =>
      queryClient.invalidateQueries({
        queryKey: databaseKey(connectionId),
      }),
    [queryClient]
  );

  const invalidateObjects = useCallback(
    (objectType: DatabaseObjectType, connectionId: string, database: string) =>
      queryClient.invalidateQueries({
        queryKey: objectKey(objectType, connectionId, database),
      }),
    [queryClient]
  );

  return {
    fetchDatabases,
    fetchObjects,
    invalidateDatabases,
    invalidateObjects,
  };
}
